import type { AnswerMap, QuizDefinition, QuizQuestion } from "@quiz/quiz-core";
import { getQuizPresentation } from "@quiz/quiz-definitions";
import { ArrowLeft, createElement } from "lucide";
import type { ResolvedUiCopy } from "../ui/copy";
import { button, el } from "../utils";
import { renderCampaignMedia } from "../components/CampaignMedia";
import { comicParchmentAvailable } from "../components/ComicQuestionAssets";

export interface QuestionScreenOptions {
  quiz: QuizDefinition;
  question: QuizQuestion;
  answers?: AnswerMap;
  selectedAnswerId?: string;
  progress: number;
  questionIndex?: number;
  totalSteps?: number;
  canGoBack: boolean;
  enterAnimation?: boolean;
  copy: ResolvedUiCopy;
  onBack: () => void;
  onAnswer: (answerId: string, button: HTMLButtonElement) => void;
}

export function renderQuestionScreen(options: QuestionScreenOptions): HTMLElement {
  const presentation = getQuizPresentation(options.quiz);
  if (presentation.comicQuest) return renderComicQuestionScreen(options);
  const screen = el("main", "screen quiz-screen");
  const compact = presentation.flowLayout === "compact";
  const progress = renderProgress(options.progress, options.copy.progressLabel);
  if (!compact) screen.append(progress);

  const shell = el("section", "question-shell");
  if (options.enterAnimation) shell.classList.add("question-shell--entering");
  const nav = el("div", "question-nav");
  const back = button("back-button", `← ${options.copy.back}`, options.onBack);
  if (compact) {
    back.replaceChildren(createElement(ArrowLeft, { width: 18, height: 18, "aria-hidden": "true" }), document.createTextNode(options.copy.back));
  }
  back.disabled = !options.canGoBack;
  nav.append(back);
  if (options.totalSteps && options.questionIndex && options.questionIndex <= options.totalSteps) {
    nav.append(el("span", "question-count", `${options.questionIndex} / ${options.totalSteps}`));
  }
  shell.append(nav);
  if (compact) shell.append(progress);

  const header = el("header", "question-header");
  header.append(el("h1", "question-title", options.question.title));
  const subtitle = resolveQuestionSubtitle(options);
  if (subtitle) header.append(el("p", "question-subtitle", subtitle));
  const visual = renderCampaignMedia(options.question.visual, "question-visual");
  if (visual) header.append(visual);
  shell.append(header);

  const answers = el("div", "answer-list");
  answers.append(
    ...options.question.answers.map((answer) => {
      const node = button("answer-card", "", () => options.onAnswer(answer.id, node));
      node.dataset.answerId = answer.id;
      const text = el("span", "answer-card__text");
      const labelRow = el("span", answer.emoji ? "answer-card__label-row" : "");
      if (answer.emoji) {
        const emoji = el("span", "answer-card__emoji", answer.emoji);
        emoji.setAttribute("aria-hidden", "true");
        labelRow.append(emoji);
      }
      labelRow.append(el("span", "answer-card__label", answer.label));
      text.append(labelRow);
      if (answer.description) text.append(el("span", "answer-description", answer.description));
      const indicator = el("span", "answer-card__indicator");
      indicator.setAttribute("aria-hidden", "true");
      node.append(text, indicator);
      if (answer.id === options.selectedAnswerId) node.classList.add("is-selected");
      return node;
    })
  );
  shell.append(answers);
  screen.append(shell);
  return screen;
}

function resolveQuestionSubtitle(options: QuestionScreenOptions): string | undefined {
  const echo = getQuizPresentation(options.quiz).questionEchoes?.[options.question.id];
  if (!echo) return options.question.subtitle;
  const answerId = options.answers?.[echo.sourceQuestionId];
  const value = answerId && echo.values[answerId];
  return value ? echo.template.replaceAll("{value}", value) : options.question.subtitle;
}

function renderComicQuestionScreen(options: QuestionScreenOptions): HTMLElement {
  const comic = getQuizPresentation(options.quiz).comicQuest;
  if (!comic) throw new Error("Missing comic quest presentation settings");

  const screen = el("main", "screen comic-question-screen");
  const canvas = el("section", "comic-question");
  const frame = comic.sceneFrames?.[options.question.id];
  if (frame) {
    canvas.classList.add("comic-question--framed");
    if (frame.offsetY !== undefined) canvas.style.setProperty("--scene-offset-y", String(frame.offsetY / 100));
    if (frame.height !== undefined) canvas.style.setProperty("--scene-height", String(frame.height / 100));
    if (frame.paperTop) canvas.style.setProperty("--scene-paper-top", frame.paperTop);
    const backdrop = options.question.visual?.src;
    if (backdrop) canvas.style.setProperty("--scene-backdrop", `url("${backdrop}")`);
  }
  const visual = renderCampaignMedia(options.question.visual, "comic-question__art");
  if (visual) canvas.append(visual);

  const hud = el("header", "comic-question__hud");
  const toolbar = el("div", "comic-question__toolbar");
  const back = button("comic-question__back", "", options.onBack);
  back.setAttribute("aria-label", options.copy.back);
  back.append(createElement(ArrowLeft, { width: 21, height: 21, "aria-hidden": "true" }));
  const logo = el("img", "comic-question__logo") as HTMLImageElement;
  logo.src = comic.logoSrc;
  logo.alt = options.quiz.metadata.name;
  logo.width = 160;
  logo.height = 40;
  const current = options.questionIndex ?? 1;
  const progress = el("div", "comic-question__progress");
  progress.setAttribute("role", "progressbar");
  progress.setAttribute("aria-label", options.copy.progressLabel);
  progress.setAttribute("aria-valuemin", "0");
  progress.setAttribute("aria-valuemax", String(comic.totalScenes));
  progress.setAttribute("aria-valuenow", String(current));
  const progressBar = el("span", "comic-question__progress-bar");
  progressBar.style.width = `${Math.min(100, (current / comic.totalScenes) * 100)}%`;
  progress.append(progressBar);
  toolbar.append(back, logo, progress, el("span", "comic-question__count", `${current} / ${comic.totalScenes}`));
  hud.append(toolbar);

  const captionText = comic.sceneCaptions?.[options.question.id];
  const caption = captionText ? el("div", "comic-question__caption") : undefined;
  if (caption && captionText) {
    const [name, epithet] = captionText.split(" · ");
    caption.append(el("strong", "comic-question__caption-name", name));
    if (epithet) caption.append(el("span", "comic-question__caption-epithet", epithet));
  }

  const paper = el("div", "comic-question__paper");
  if (comic.parchmentSrc) paper.style.setProperty("--parchment-image", `url("${comic.parchmentSrc}")`);
  if (!comicParchmentAvailable(comic.parchmentSrc)) paper.classList.add("comic-question__paper--fallback");
  if (options.question.subtitle) paper.append(el("p", "comic-question__story", options.question.subtitle));
  paper.append(el("h1", "comic-question__prompt", options.question.title));
  const answers = el("div", "comic-question__answers");

  options.question.answers.forEach((answer, index) => {
    const answerNode = button("comic-answer", "", () => {
      if (answers.dataset.locked === "true") return;
      answers.dataset.locked = "true";
      answers.querySelectorAll(".comic-answer").forEach((node) => node.classList.remove("is-selected"));
      answerNode.classList.add("is-selected");
      options.onAnswer(answer.id, answerNode);
    });
    answerNode.dataset.answerId = answer.id;
    answerNode.append(
      el("span", "comic-answer__letter", String.fromCharCode(65 + index)),
      el("span", "comic-answer__label", answer.label)
    );
    if (answer.id === options.selectedAnswerId) answerNode.classList.add("is-selected");
    answers.append(answerNode);
  });
  paper.append(answers);
  canvas.append(hud);
  if (caption) canvas.append(caption);
  canvas.append(paper);
  screen.append(canvas);
  return screen;
}

function renderProgress(percent: number, label: string): HTMLElement {
  const wrap = el("div", "top-progress");
  const meta = el("div", "top-progress__meta");
  meta.append(el("span", "", label), el("strong", "", `${percent}%`));
  const track = el("div", "top-progress__track");
  track.setAttribute("role", "progressbar");
  track.setAttribute("aria-label", label);
  track.setAttribute("aria-valuemin", "0");
  track.setAttribute("aria-valuemax", "100");
  track.setAttribute("aria-valuenow", String(percent));
  const bar = el("div", "top-progress__bar");
  bar.style.width = `${percent}%`;
  track.append(bar);
  wrap.append(meta, track);
  return wrap;
}
